function keepToken(cleaned) {
    if (cleaned.length === 0) return false;
    if (/^\d+$/.test(cleaned)) return cleaned.length >= 2;
    if (/^\d+(g|kg|ml|l|oz|mg|lb)s?$/i.test(cleaned)) return cleaned.length >= 2;
    if (/^\d+x\d+(g|kg|ml|l|oz|mg|lb)?s?$/i.test(cleaned)) return true;
    if (cleaned.length === 2) return /[a-z]/i.test(cleaned);
    return cleaned.length >= 3;
}

function compactAlnum(text) {
    return String(text ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

function normalizeTokenizerInput(text) {
    return String(text ?? '').replace(/\bm\s*and\s*m\b/ig, 'm&m');
}

function addSupplementaryTokens(part, tokens, allowSingleLetter) {
    const lower = part.toLowerCase().trim();
    if (!lower) return;

    const unitMatch = lower.match(/^(\d+)(g|gm|kg|ml|l|oz|mg|lb)$/i);
    if (unitMatch && keepToken(unitMatch[1])) {
        tokens.add(unitMatch[1]);
    }

    if (/^[a-z0-9]+([&/+.-][a-z0-9]+)+$/i.test(lower)) {
        const joined = compactAlnum(lower);
        if (keepToken(joined)) tokens.add(joined);
    }

    const compact = compactAlnum(lower);
    if (compact && keepToken(compact)) {
        tokens.add(compact);
    }

    const pieces = compact.match(/[a-z]+|\d+/g) || [];
    for (const piece of pieces) {
        if (keepToken(piece)) {
            tokens.add(piece);
            continue;
        }
        if (allowSingleLetter && piece.length === 1 && /[a-z]/i.test(piece)) {
            tokens.add(piece);
        }
    }
}

function tokensFromPart(part, allowSingleLetter = false) {
    const lower = part.toLowerCase();
    const hasSpecial = /[^a-z0-9]/i.test(part);

    if (!hasSpecial) {
        if (keepToken(lower)) {
            return [lower];
        }
        if (allowSingleLetter && lower.length === 1 && /[a-z]/i.test(lower)) {
            return [lower];
        }
        return [];
    }

    const packMatch = part.match(/\d+\s*x\s*\d+\s*(?:g|kg|ml|l|oz|mg|lb)?/i);
    if (packMatch) {
        const pack = packMatch[0].replace(/\s/g, '').toLowerCase();
        return pack.length >= 2 ? [pack] : [];
    }

    if (/^[(\[]/.test(part)) {
        const lit = lower.match(/^[(\[]+\d+[a-z0-9]*/);
        if (lit && lit[0].length >= 2) {
            return [lit[0]];
        }
        if (lower.length >= 2) {
            return [lower];
        }
        return [];
    }

    if (/^[^a-z0-9]+[a-z0-9]/i.test(part)) {
        const lit = lower.match(/^[^a-z0-9]+[a-z0-9]+[^a-z0-9]*/);
        if (lit && lit[0].length >= 2) {
            return [lit[0]];
        }
    }

    if (/^[^a-z0-9]*\d/.test(part)) {
        const prefix = lower.match(/^[^a-z0-9]*\d+[a-z0-9]*/);
        if (prefix && prefix[0].length >= 2) {
            return [prefix[0]];
        }
    }

    if (part.includes('/')) {
        return lower.length >= 2 ? [lower] : [];
    }

    const compact = compactAlnum(part);
    const stripped = part.replace(/^[^a-z0-9]+/i, '').replace(/[^a-z0-9]+$/i, '').toLowerCase();

    if (compact.length >= 2 && keepToken(compact)) {
        return [compact];
    }

    if (/[a-z0-9]/i.test(part) && stripped.length >= 2) {
        return [stripped];
    }

    if (/[a-z0-9]/i.test(part) && lower.length >= 2) {
        return [lower];
    }

    return [];
}

export function tokenize(text) {
    if (typeof text !== 'string' || text.trim() === '') {
        return [];
    }

    const tokens = new Set();
    const normalized = normalizeTokenizerInput(text);
    const parts = normalized.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const allowSingleLetter = parts.length > 1;

    for (const part of parts) {
        if (part === '') continue;
        for (const token of tokensFromPart(part, allowSingleLetter)) {
            tokens.add(token);
        }
        addSupplementaryTokens(part, tokens, allowSingleLetter);
    }

    return Array.from(tokens);
}
